import { useEffect } from 'react';
import { Route, Routes, useNavigate, useLocation } from 'react-router-dom';

import Home from '@/pages/Home';
import DashboardDetail from '@/pages/DashboardDetail';
import Login from '@/pages/Login';
import ResetPassword from '@/pages/ResetPassword';
import DefinePassword from '@/pages/DefinePassword';
import RecoverPassword from '@/pages/RecoverPassword';
import ProviderRegister from '@/pages/ProviderRegister';
import BankUserRegister from '@/pages/BankUserRegister';
import RequestInspection from '@/pages/RequestInspection';
import ProviderUserRegister from '@/pages/ProviderUserRegister';
import InspectorUserRegister from '@/pages/InspectorUserRegister';
import ChecklistsIndex from '@/pages/ChecklistsIndex';
import ChecklistNew from '@/pages/ChecklistNew';
import { ProviderCompanyList } from '@/pages/ProviderCompanyList';
import { ProviderCompanyEdit } from '@/pages/ProviderCompanyEdit';
import { BankUserList } from '@/pages/BankUserList';
import { BankUserEdit } from '@/pages/BankUserEdit';
import { ProviderUserList } from '@/pages/ProviderUserList';
import { ProviderUserEdit } from '@/pages/ProviderUserEdit';
import { InspectorUserList } from '@/pages/InspectorUserList';
import { InspectorUserEdit } from '@/pages/InspectorUserEdit';
import { Profile } from '@/pages/Profile';
import { GuestVideoCall } from '@/pages/GuestVideoCall';
import { InspectionTypeList } from '@/pages/InspectionTypeList';
import { PropertyTypeList } from '@/pages/PropertyTypeList';
import { InspectionReasonList } from '@/pages/InspectionReasonList';
import { InspectionIssueTypeParamList } from '@/pages/InspectionIssueTypeParamList';
import Calendar from '@/pages/Calendar';
import Reports from '@/pages/Reports';

import { useAuth } from '@/contexts/auth.context';
import { RouteGuard } from '@/components/PermissionGuard';
import { UserPermissions } from '@/shared/enums/UserPermissions';
import { BankRoles, ProviderRoles } from '@/shared/enums/UserRoles';
import { isPublicRoute } from '@/shared/config/routePermissions';

export function AppRoutes() {
  const navigation = useNavigate();
  const location = useLocation();

  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated && !isPublicRoute(location.pathname)) {
      navigation('/login');
    } else if (isAuthenticated && location.pathname === '/login') {
      navigation('/');
    }
  }, [isAuthenticated, location.pathname, navigation]);

  return (
    <Routes>
      {}
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/define-password/:token" element={<DefinePassword />} />
      <Route path="/recover-password" element={<RecoverPassword />} />
      <Route path="/guest/video-call" element={<GuestVideoCall />} />

      {}
      <Route
        path="/"
        element={<Home />}
      />

      {}
      <Route
        path="/dashboard/:cardType"
        element={
          <RouteGuard
            permissions={[
              UserPermissions.home_view,
              UserPermissions.home_view_provider,
              UserPermissions.home_view_inspector
            ]}
          >
            <DashboardDetail />
          </RouteGuard>
        }
      />

      {}
      <Route 
        path="/bank-register" 
        element={
          <RouteGuard 
            permissions={[UserPermissions.users_create_bank]}
            roles={[BankRoles.ADMIN_BANK, BankRoles.ACCESS_MANAGER]}
          >
            <BankUserRegister />
          </RouteGuard>
        } 
      />
      
      <Route 
        path="/provider-register" 
        element={
          <RouteGuard 
            permissions={[UserPermissions.users_create_provider]}
            roles={[ProviderRoles.MASTER_PROVIDER]}
          >
            <ProviderUserRegister />
          </RouteGuard>
        } 
      />
      
      <Route 
        path="/inspector-register" 
        element={
          <RouteGuard 
            permissions={[
              UserPermissions.inspectors_create_provider, 
              UserPermissions.inspectors_create_bank
            ]}
            roles={[BankRoles.ADMIN_BANK, BankRoles.ACCESS_MANAGER, ProviderRoles.MASTER_PROVIDER]}
          >
            <InspectorUserRegister />
          </RouteGuard>
        } 
      />

      {}
      <Route 
        path="/vendor-register" 
        element={
          <RouteGuard 
            permissions={[UserPermissions.providers_create_bank]}
            roles={[BankRoles.ADMIN_BANK]}
          >
            <ProviderRegister />
          </RouteGuard>
        } 
      />

      {}
      <Route 
        path="/request-inspection" 
        element={
          <RouteGuard 
            permissions={[UserPermissions.inspections_create_bank]}
            roles={[BankRoles.ADMIN_BANK]}
          >
            <RequestInspection />
          </RouteGuard>
        } 
      />

      {}
      <Route 
        path="/checklists" 
        element={
          <RouteGuard 
            permissions={[
              UserPermissions.checklist_read_inspector,
              UserPermissions.checklist_read_provider,
              UserPermissions.checklist_read_bank
            ]}
          >
            <ChecklistsIndex />
          </RouteGuard>
        } 
      />
      
      <Route 
        path="/checklist/new" 
        element={
          <RouteGuard 
            permissions={[UserPermissions.checklist_create_bank]}
            roles={[BankRoles.ADMIN_BANK]}
          >
            <ChecklistNew />
          </RouteGuard>
        } 
      />
      
      <Route
        path="/checklist/edit/:id"
        element={
          <RouteGuard
            permissions={[UserPermissions.checklist_update_bank]}
            roles={[BankRoles.ADMIN_BANK, BankRoles.BANK_OPERATIONAL]}
          >
            <ChecklistNew />
          </RouteGuard>
        }
      />

      {}
      <Route
        path="/vendor-list"
        element={
          <RouteGuard
            permissions={[UserPermissions.providers_read_bank]}
            roles={[BankRoles.ADMIN_BANK, BankRoles.ACCESS_MANAGER]}
          >
            <ProviderCompanyList />
          </RouteGuard>
        }
      />

      <Route
        path="/vendor-edit/:id"
        element={
          <RouteGuard
            permissions={[UserPermissions.providers_update_bank]}
            roles={[BankRoles.ADMIN_BANK]}
          >
            <ProviderCompanyEdit />
          </RouteGuard>
        }
      />

      {}
      <Route
        path="/bank-list"
        element={
          <RouteGuard
            permissions={[UserPermissions.users_read_bank]}
            roles={[BankRoles.ADMIN_BANK, BankRoles.ACCESS_MANAGER]}
          >
            <BankUserList />
          </RouteGuard>
        }
      />

      <Route
        path="/bank-edit/:id"
        element={
          <RouteGuard
            permissions={[UserPermissions.users_update_bank]}
            roles={[BankRoles.ADMIN_BANK, BankRoles.ACCESS_MANAGER]}
          >
            <BankUserEdit />
          </RouteGuard>
        }
      />

      {}
      <Route
        path="/provider-list"
        element={
          <RouteGuard
            permissions={[UserPermissions.users_read_provider, UserPermissions.users_read_bank]}
            roles={[BankRoles.ADMIN_BANK, BankRoles.ACCESS_MANAGER, ProviderRoles.MASTER_PROVIDER]}
          >
            <ProviderUserList />
          </RouteGuard>
        }
      />

      <Route
        path="/provider-edit/:id"
        element={
          <RouteGuard
            permissions={[UserPermissions.users_update_provider, UserPermissions.users_update_bank]}
            roles={[BankRoles.ADMIN_BANK, BankRoles.ACCESS_MANAGER, ProviderRoles.MASTER_PROVIDER]}
          >
            <ProviderUserEdit />
          </RouteGuard>
        }
      />

      {}
      <Route
        path="/inspector-list"
        element={
          <RouteGuard
            permissions={[
              UserPermissions.inspectors_read_provider,
              UserPermissions.inspectors_read_bank
            ]}
            roles={[BankRoles.ADMIN_BANK, BankRoles.ACCESS_MANAGER, ProviderRoles.MASTER_PROVIDER]}
          >
            <InspectorUserList />
          </RouteGuard>
        }
      />

      <Route
        path="/inspector-edit/:id"
        element={
          <RouteGuard
            permissions={[
              UserPermissions.inspectors_update_provider,
              UserPermissions.inspectors_update_bank
            ]}
            roles={[BankRoles.ADMIN_BANK, BankRoles.ACCESS_MANAGER, ProviderRoles.MASTER_PROVIDER]}
          >
            <InspectorUserEdit />
          </RouteGuard>
        }
      />

      {}
      <Route
        path="/inspection-type-list"
        element={
          <RouteGuard
            permissions={[UserPermissions.inspections_create_bank]}
            roles={[BankRoles.ADMIN_BANK]}
          >
            <InspectionTypeList />
          </RouteGuard>
        }
      />

      <Route
        path="/property-type-list"
        element={
          <RouteGuard
            permissions={[UserPermissions.inspections_create_bank]}
            roles={[BankRoles.ADMIN_BANK]}
          >
            <PropertyTypeList />
          </RouteGuard>
        }
      />

      <Route
        path="/inspection-reason-list"
        element={
          <RouteGuard
            permissions={[UserPermissions.inspections_create_bank]}
            roles={[BankRoles.ADMIN_BANK]}
          >
            <InspectionReasonList />
          </RouteGuard>
        }
      />

      <Route
        path="/inspection-issue-type-list"
        element={
          <RouteGuard
            permissions={[UserPermissions.inspections_create_bank]}
            roles={[BankRoles.ADMIN_BANK]}
          >
            <InspectionIssueTypeParamList />
          </RouteGuard>
        }
      />

      {}
      <Route
        path="/calendar"
        element={
          <RouteGuard
            permissions={[
              UserPermissions.home_view,
              UserPermissions.home_view_provider,
              UserPermissions.home_view_inspector
            ]}
          >
            <Calendar />
          </RouteGuard>
        }
      />

      {}
      <Route
        path="/metrics"
        element={
          <RouteGuard
            permissions={[
              UserPermissions.home_view,
              UserPermissions.home_view_provider
            ]}
            roles={[
              BankRoles.ADMIN_BANK,
              ProviderRoles.MASTER_PROVIDER
            ]}
          >
            <Reports />
          </RouteGuard>
        }
      />

      {}
      <Route
        path="/profile"
        element={<Profile />}
      />
    </Routes>
  );
}